import { createSlice } from '@reduxjs/toolkit';

import { DROPPABLE_TARGET_TYPE } from '@pages/organizer/Cloud/const/droppable_target-type';
import { CLOUD_THUNKS } from '@pages/organizer/Cloud/store/cloud-store/cloud-thunks';
import { detailsFileData } from '@pages/organizer/Cloud/utils/details-info/details-file-data';
import { detailsFolderData } from '@pages/organizer/Cloud/utils/details-info/details-folder-data';
import { isAlreadySelected } from '@pages/organizer/Cloud/utils/multi-selected/is-already-selected';

import type {
  DetailInfoType,
  DroppableDataType,
  FileType,
  FiltersType,
  FolderType,
  ItemsList,
  ItemsType,
  MetaType,
  RootDataType,
} from '@pages/organizer/Cloud/store/cloud-store/cloud-types';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Nullable } from '@shared/types/base-custom.type';

const emptyStateFilters = {
  type: null,
  q: null,
  q_enter: null,
};

const initialState = {
  isOverFolder: false,
  isLoaded: false,

  details_data: null as Nullable<DetailInfoType>,
  filters: {
    type: null,
    q: null,
    q_enter: null,
  } as FiltersType,

  dropdownData: {
    searchData: [] as ItemsList,
    metaData: null as Nullable<MetaType>,
  },

  data: {} as ItemsType,
  meta: {} as MetaType,

  selected: [] as ItemsList,
  disabledSelectedIds: {} as Record<string, boolean>,
};

const cloudSlice = createSlice({
  name: 'cloudSlice',
  initialState,
  reducers: {
    // multiple- selected

    removeSelectedItems: (state) => {
      state.data.items = state.data.items.filter((item) => !(item.id in state.disabledSelectedIds));
    },
    setDraggingState: (state, action: PayloadAction<boolean>) => {
      Object.keys(state.disabledSelectedIds).forEach((id) => {
        state.disabledSelectedIds[id] = action.payload;
      });
    },
    selectedMultiple: (
      state,
      action: PayloadAction<{ selectedItem: FolderType | FileType; isMultiple: boolean }>,
    ) => {
      const selected = action.payload.selectedItem;
      const isMultipleSelected = action.payload.isMultiple;
      const alreadySelected = state.selected;
      if (isMultipleSelected) {
        if (isAlreadySelected(selected, alreadySelected)) {
          const index = state.selected.findIndex((select) => select.id === selected.id);
          if (index !== -1) {
            state.selected.splice(index, 1);
            delete state.disabledSelectedIds[selected.id];
          }

          return;
        }

        state.selected.push(selected);
        state.disabledSelectedIds[selected.id] = false;
      } else {
        state.selected = [selected];
        state.disabledSelectedIds = { [selected.id]: false };
      }
    },

    clearSelected: (state) => {
      state.selected = initialState.selected;
      state.disabledSelectedIds = initialState.disabledSelectedIds;
    },

    loadedCloud: (state, action: PayloadAction<{ loaded: boolean }>) => {
      state.isLoaded = action.payload.loaded;
    },
    setFilters: (state, action: PayloadAction<Partial<FiltersType>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = { ...state.filters, ...emptyStateFilters };
    },

    setDropdownData: (
      state,
      action: PayloadAction<
        Partial<{
          searchData: ItemsList;
          metaData: Nullable<MetaType>;
        }>
      >,
    ) => {
      state.dropdownData = { ...state.dropdownData, ...action.payload };
    },
    pushDropdownData: (state, action: PayloadAction<ItemsList>) => {
      state.dropdownData.searchData.push(...action.payload);
    },

    setItems: (state, action: PayloadAction<RootDataType>) => {
      state.data = action.payload.data;
      state.meta = action.payload.meta;
      state.isLoaded = true;
    },
    setPagination: (state, action: PayloadAction<RootDataType>) => {
      state.data.items.push(...action.payload.data.items);
      state.meta = action.payload.meta;
    },
    // --------------------- folders ------------------------------

    setIsOverFolder: (state, action: PayloadAction<{ isOver: boolean }>) => {
      state.isOverFolder = action.payload.isOver;
    },

    updateFolder: (state, action: PayloadAction<{ updateFolder: { name: string }; itemID: string }>) => {
      const folderIndex = state.data.items.findIndex((folder) => folder.id === action.payload.itemID);
      if (folderIndex !== -1)
        (state.data.items[folderIndex] as FolderType).folder = {
          ...(state.data.items[folderIndex] as FolderType).folder,
          ...action.payload.updateFolder,
        };
    },
    removeFolder: (state, action: PayloadAction<{ itemID: string }>) => {
      const folderIndex = state.data.items.findIndex((folder) => folder.id === action.payload.itemID);
      if (folderIndex !== -1) state.data.items.splice(folderIndex, 1);
    },
    DragNDrop: (state, action: PayloadAction<{ dndData: DroppableDataType }>) => {
      const { targetType, targetID } = action.payload.dndData;

      switch (targetType) {
        case DROPPABLE_TARGET_TYPE.FOLDER_IN_FOLDER: {
          const findTargetFolderIndex = state.data.items.findIndex((folder) => folder.id === targetID);

          if (findTargetFolderIndex !== -1) {
            state.data.items.splice(findTargetFolderIndex, 1);
          }
          break;
        }
        case DROPPABLE_TARGET_TYPE.FILE_IN_FOLDER:
          {
            const findTargetFileIndex = state.data.items.findIndex((file) => file.id === targetID);

            if (findTargetFileIndex !== -1) {
              state.data.items.splice(findTargetFileIndex, 1);
            }
          }
          return;
        case DROPPABLE_TARGET_TYPE.SYSTEM_IN_FOLDER: {
          break;
        }
        default: {
          break;
        }
      }
    },

    // --------------------- info ------------------------------

    clearDetailsInfo: (state) => {
      state.details_data = null;
    },

    // --------------------- files ------------------------------

    updateFile: (state, action: PayloadAction<{ updateFile: { name: string }; itemID: string }>) => {
      const fileIndex = state.data.items.findIndex((folder) => folder.id === action.payload.itemID);
      if (fileIndex !== -1)
        (state.data.items[fileIndex] as FileType).file = {
          ...(state.data.items[fileIndex] as FileType).file,
          ...action.payload.updateFile,
        };
    },
    removeFile: (state, action: PayloadAction<{ itemID: string }>) => {
      const fileIndex = state.data.items.findIndex((file) => file.id === action.payload.itemID);
      if (fileIndex !== -1) state.data.items.splice(fileIndex, 1);
    },

    progressFile: (state, action: PayloadAction<{ id: string; progress: number }>) => {
      const fileIndex = state.data.items.findIndex((file) => file.id === action.payload.id);
      if (fileIndex !== -1) (state.data.items[fileIndex] as FileType).progress = action.payload.progress;
    },
    replaceFile: (state, action: PayloadAction<{ file: FileType; replaceFileID: string }>) => {
      const fileIndex = state.data.items.findIndex((file) => file.id === action.payload.replaceFileID);
      if (fileIndex !== -1) (state.data.items[fileIndex] as FileType) = action.payload.file;
    },

    addFilePreview: (state, action: PayloadAction<any>) => {
      if (action.payload.isDnd) return;
      const index = state.data.items.findIndex(
        (item) => (item as FileType).progressID === action.payload.progressID,
      );

      if (index !== -1) {
        state.data.items[index] = action.payload;
      } else {
        state.data.items.unshift(action.payload);
      }
    },

    setFile: (state, action: PayloadAction<{ file: FileType; isDnd: boolean }>) => {
      if (action.payload.isDnd) return;

      state.data.items.unshift(action.payload.file);
    },
    setDetailsData: (state, action: PayloadAction<{ data: FolderType | FileType }>) => {
      const ownerID = state.data.self.folder.owner_id;
      state.details_data =
        action.payload.data.type === 'folder'
          ? detailsFolderData(action.payload.data)
          : detailsFileData(action.payload.data, ownerID);
    },

    resetAllFilters: (state) => {
      cloudSlice.caseReducers.clearDetailsInfo(state);
      cloudSlice.caseReducers.clearFilters(state);
      cloudSlice.caseReducers.setDropdownData(state, {
        payload: { searchData: [], metaData: null },
        type: 'setDropdownData',
      });
      cloudSlice.caseReducers.clearSelected(state);
    },
    resetStore: (state) => {
      state.data = initialState.data as ItemsType;
      state.meta = initialState.meta as MetaType;
      state.selected = initialState.selected;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(CLOUD_THUNKS.createFolderAsync.fulfilled, (state, action) => {
      state.data.items.unshift(action.payload.data);
    });
  },
});

export const cloudReducer = cloudSlice.reducer;

export const {
  updateFolder,
  removeFolder,
  updateFile,
  removeFile,
  DragNDrop,
  setDetailsData,
  clearDetailsInfo,
  loadedCloud,
  setIsOverFolder,
  resetAllFilters,
  setItems,
  setPagination,
  setFilters,
  clearFilters,
  replaceFile,
  setDropdownData,
  pushDropdownData,
  setFile,
  selectedMultiple,
  clearSelected,
  setDraggingState,
  resetStore,
  removeSelectedItems,
} = cloudSlice.actions;
